import { ApiError } from './errors'
import { SUPPORT_EMAIL } from './legal'

/**
 * What to tell a photographer when an upload is refused for a Memoir reason
 * (SOL-204), or null when the error is not one of those.
 *
 * One mapping for both upload widgets: the folder upload (use-album-upload)
 * and the toolbar's new-album upload (upload-interface). Two copies is how the
 * read-only explanation would drift — and the SOL-134 lesson was that a
 * read-only refusal falling through to a generic "something went wrong" sends
 * the user looking for a bug instead of the reason.
 *
 * Copy rules: never a size or a GB figure (the abuse ceiling is operational,
 * SOL-199), and "Memoir" only as a countable noun with an article.
 */
export function memoirUploadRefusal(error: unknown): { title: string; message: string } | null {
  if (!(error instanceof ApiError)) return null
  switch (error.code) {
    case 'MEMOIR_READONLY':
      return {
        title: 'Uploads are paused',
        // Neutral on purpose: a refund, a dispute, a lapsed year and a paid
        // read-only year all answer this code; the Memoir's page says which.
        message:
          'This Memoir is read-only right now, so new uploads to it are paused. ' +
          'Its photos are safe and can still be viewed, shared and downloaded. Your other Memoirs are not affected.',
      }
    case 'MEMOIR_LIMIT_EXCEEDED':
      return {
        title: 'This Memoir is full',
        message: `This Memoir cannot take any more photos. Please write to ${SUPPORT_EMAIL}.`,
      }
    case 'MEMOIR_UNMEASURABLE':
      return {
        title: 'Please try again shortly',
        message: 'We could not verify this Memoir just now. Nothing was lost; try the upload again in a few minutes.',
      }
    case 'MEMOIR_REQUIRED':
    case 'MEMOIR_NOT_FOUND':
      return {
        title: 'This album has no Memoir',
        message: 'Albums now live inside a Memoir. Open one of your Memoirs and create the album there.',
      }
    case 'ACCOUNT_CLOSING':
      return {
        title: 'Your account is being deleted',
        message:
          'You asked to delete your account, so uploads, purchases and redeeming are paused. ' +
          `Cancel the deletion from the Account page to carry on, or write to ${SUPPORT_EMAIL}.`,
      }
    case 'ACCOUNT_LOCKED':
      return {
        title: 'Your account is about to be deleted',
        message:
          'It is the last day before your account is deleted, so nothing but cancelling is available. ' +
          `Cancel the deletion to get your photos back, or write to ${SUPPORT_EMAIL}.`,
      }
    default:
      return null
  }
}
